import useGetMe from '@/api/queries/me/useGetMe';
import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import { Timelog } from '@/api/queries/timelog/useGetTimelogs';
import { humanizeCurrency } from '@/helpers/currency';
import { useUserHasPermission } from '@/hooks/usePermissions';
import { ManageableItemsList, Tooltip, UserAvatar } from '@payaca/ambrosia';

import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';
import { TimelogsPermissions } from '@payaca/permissions/timelogs/timelogs.permissions';
import { SortDirection } from '@payaca/types/listViewTypes';
import { format } from 'date-fns';
import { parse, toSeconds } from 'iso8601-duration';
import { FC } from 'react';
import { useListedTimelogsContext } from './listedTimelogsContext';

const timelogSortByToFieldMap = {
  ASSIGNEE_NAME: 'user' as const,
  CALCULATED_COST: 'cost' as const,
  DURATION: 'duration' as const,
  END_TIME: 'endTime' as const,
  START_TIME: 'startTime' as const,
  TYPE: 'type' as const,
};

const isoDurationToReadableString = (isoDuration: string) => {
  let totalSeconds = 0;

  try {
    const duration = parse(isoDuration);

    totalSeconds = toSeconds(duration);
  } catch (e) {}

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const formatted = `${String(hours).padStart(2, '0')}:${String(
    minutes
  ).padStart(2, '0')} hours`;

  return formatted;
};

export const ListedTimelogs: FC = () => {
  const {
    queryResult: getTimelogsQueryResult,
    pagination,
    setSort,
    setCurrentPage,
    input,
    openDialog,
  } = useListedTimelogsContext();

  const { user } = useGetMe();
  const { data: regionPreferences } = useGetMyRegionPreferences();

  const hasCostPermission = useUserHasPermission({
    permissions: [TimelogsPermissions.GET_TIMELOG_COST_PER_HOUR],
  });

  const canEditAllTimelogs = useUserHasPermission({
    permissions: [TimelogsPermissions.UPDATE_TIMELOGS],
  });

  const canEditOwnTimelogs = useUserHasPermission({
    permissions: [TimelogsPermissions.UPDATE_SELF_ASSIGNED_TIMELOGS],
  });

  const canDeleteAllTimelogs = useUserHasPermission({
    permissions: [TimelogsPermissions.DELETE_TIMELOGS],
  });

  const canDeleteOwnTimelogs = useUserHasPermission({
    permissions: [TimelogsPermissions.DELETE_SELF_ASSIGNED_TIMELOGS],
  });

  if (!getTimelogsQueryResult) return null;

  const { isLoading, data } = getTimelogsQueryResult;

  const items = data?.timelogs?.items || [];

  const showPaginationRequired =
    data?.timelogs && data.timelogs.totalCount > pagination.limit;

  return (
    <>
      <ManageableItemsList.Table
        emptyText="No timelogs added yet"
        uniqueKey="id"
        isLoading={isLoading}
        items={items}
        sortBy={
          input.sortBy && input.sortBy !== 'CREATED_AT'
            ? timelogSortByToFieldMap[input.sortBy]
            : undefined
        }
        onClickRow={(item) => {
          if (
            canEditAllTimelogs ||
            (canEditOwnTimelogs && item.user?.id === user?.id)
          ) {
            openDialog({
              type: 'editTimelog',
              timelogId: item.id,
            });
          }
        }}
        itemActions={[
          {
            label: 'Edit',
            onClick: (row) =>
              openDialog({
                type: 'editTimelog',
                timelogId: row.id,
              }),
            isDisabled: (row) =>
              !(
                canEditAllTimelogs ||
                (canEditOwnTimelogs && row.user?.id === user?.id)
              ),
            disabledReasons: [
              "You don't have permission to perform this action",
            ],
          },
          {
            label: 'Delete',
            onClick: (row) =>
              openDialog({
                type: 'deleteTimelog',
                timelogId: row.id,
              }),
            isDisabled: (row) =>
              !(
                canDeleteAllTimelogs ||
                (canDeleteOwnTimelogs && row.user?.id === user?.id)
              ),
            disabledReasons: [
              "You don't have permission to perform this action",
            ],
          },
        ]}
        sortDirection={
          input.sortDirection === 'ASC'
            ? SortDirection.ASCENDING
            : input.sortDirection === 'DESC'
              ? SortDirection.DESCENDING
              : undefined
        }
        footerColumns={
          data?.timelogs
            ? [
                {
                  span: 4,
                  render: () => 'Total',
                },
                {
                  span: 1,
                  render: () => (
                    <span className="prose whitespace-nowrap normal-case">
                      {data.timelogs.totalDuration
                        ? isoDurationToReadableString(
                            data.timelogs.totalDuration
                          )
                        : ''}
                    </span>
                  ),
                },
                ...(hasCostPermission
                  ? [
                      {
                        span: 1,
                        className: 'text-right',
                        render: () =>
                          data.timelogs.totalCost ? (
                            <span className="prose whitespace-nowrap">
                              {humanizeCurrency(data.timelogs.totalCost)}
                            </span>
                          ) : null,
                      },
                    ]
                  : []),
              ]
            : undefined
        }
      >
        <ManageableItemsList.Table.Column
          header="User"
          field="user"
          onSort={(field, sortDirection) =>
            setSort({
              sortBy: 'ASSIGNEE_NAME',
              sortDirection:
                sortDirection === SortDirection.ASCENDING ? 'ASC' : 'DESC',
            })
          }
          render={(user) => {
            if (!user) return null;

            return (
              <UserAvatar
                enableUserInfoTooltip={true}
                sizeVariant="xs"
                borderColourVariant="white"
                user={{
                  firstName: user.firstName,
                  lastName: user.lastName,
                  emailAddress: user.email,
                  userColour: user.colour,
                  imgSrc: user.avatarUrl,
                }}
              />
            );
          }}
        />
        <ManageableItemsList.Table.Column
          header="Type"
          field="type"
          onSort={(field, sortDirection) =>
            setSort({
              sortBy: 'TYPE',
              sortDirection:
                sortDirection === SortDirection.ASCENDING ? 'ASC' : 'DESC',
            })
          }
          render={(type) => type?.name}
        />
        <ManageableItemsList.Table.Column<Timelog, 'startTime'>
          header="Start time"
          field="startTime"
          onSort={(field, sortDirection) =>
            setSort({
              sortBy: 'START_TIME',
              sortDirection:
                sortDirection === SortDirection.ASCENDING ? 'ASC' : 'DESC',
            })
          }
          render={(startTime) => (
            <Tooltip
              tooltipContent={format(
                startTime,
                getInternationalDateFormatByLocale(
                  DateFormats.TIMESTAMP,
                  regionPreferences?.locale
                )
              )}
            >
              {format(
                startTime,
                getInternationalDateFormatByLocale(
                  DateFormats.TIMESTAMP_DAY_MONTH,
                  regionPreferences?.locale
                )
              )}
            </Tooltip>
          )}
        />
        <ManageableItemsList.Table.Column<Timelog, 'endTime'>
          header="End time"
          field="endTime"
          onSort={(field, sortDirection) =>
            setSort({
              sortBy: 'END_TIME',
              sortDirection:
                sortDirection === SortDirection.ASCENDING ? 'ASC' : 'DESC',
            })
          }
          render={(endTime) => {
            if (!endTime) {
              return '--';
            }

            return (
              <Tooltip
                tooltipContent={format(
                  endTime,
                  getInternationalDateFormatByLocale(
                    DateFormats.TIMESTAMP,
                    regionPreferences?.locale
                  )
                )}
              >
                {format(
                  endTime,
                  getInternationalDateFormatByLocale(
                    DateFormats.TIMESTAMP_DAY_MONTH,
                    regionPreferences?.locale
                  )
                )}
              </Tooltip>
            );
          }}
        />

        <ManageableItemsList.Table.Column
          header="Duration"
          field="duration"
          onSort={(field, sortDirection) =>
            setSort({
              sortBy: 'DURATION',
              sortDirection:
                sortDirection === SortDirection.ASCENDING ? 'ASC' : 'DESC',
            })
          }
          render={(duration) => {
            return isoDurationToReadableString(duration);
          }}
        />

        {hasCostPermission && (
          <ManageableItemsList.Table.Column<Timelog, 'cost'>
            header="Cost"
            field="cost"
            align="end"
            onSort={(field, sortDirection) =>
              setSort({
                sortBy: 'CALCULATED_COST',
                sortDirection:
                  sortDirection === SortDirection.ASCENDING ? 'ASC' : 'DESC',
              })
            }
            render={(cost) => (cost ? humanizeCurrency(cost) : null)}
          />
        )}
      </ManageableItemsList.Table>
      {data?.timelogs && showPaginationRequired && (
        <ManageableItemsList.PaginationBar
          type="from-paginated-result"
          {...data.timelogs}
          onPageChange={setCurrentPage}
        />
      )}
    </>
  );
};
